import { getCallDiagnostics } from '@/services/logger/call-diagnostics';
import { createLogger } from '@/services/logger/logger';
import { tracingService } from '@/services/tracing/tracing-service';
import { CallEventLevel, CallEventName } from '@/services/tracing/types';
import { useCallback, useEffect } from 'react';
import { useVonageClient } from '../../vonage-client-provider';
import { serializeError } from '../error-utils';
import { useVoiceCallState } from '../voice-call-container';

const log = createLogger('Earmuff', ['calls']);

/**
 * @internal
 * This hook is used internally by VoiceCallActionsProvider.
 * Consumers should use useVoiceCallActions() instead.
 */
export const useEarmuff = () => {
  const vonageClient = useVonageClient();
  const [call, callDispatch] = useVoiceCallState();

  useEffect(() => {
    const handleEarmuff = vonageClient.on('earmuff', (callId, legId, earmuffed) => {
      if (callId !== legId) {
        return;
      }
      if (call.callId !== callId) {
        return;
      }
      callDispatch({ type: 'call:earmuff:updated', earmuffed });
    });

    return () => {
      vonageClient.off('earmuff', handleEarmuff);
    };
  }, [callDispatch, call.callId, vonageClient]);

  const toggleEarmuff = useCallback(async () => {
    if (!call.callId) {
      return;
    }
    if (call.callState !== 'in-progress') {
      return;
    }

    const diagnostics = await getCallDiagnostics();

    log.info('Earmuff toggle attempt', {
      callId: call.callId,
      earmuffed: call.earmuffed,
      timestamp: Date.now(),
      diagnostics,
    });
    tracingService.trackEvent(CallEventName.AttemptEarmuf, CallEventLevel.Info, {
      deviceLegId: call.callId,
      earmuffed: call.earmuffed,
    });

    try {
      if (call.earmuffed) {
        await vonageClient.disableEarmuff(call.callId);
      } else {
        await vonageClient.enableEarmuff(call.callId);
      }
    } catch (error) {
      log.error('Earmuff toggle failed', {
        callId: call.callId,
        earmuffed: call.earmuffed,
        ...serializeError(error),
        timestamp: Date.now(),
        diagnostics,
      });
      throw error;
    }
  }, [call.callId, call.callState, call.earmuffed, vonageClient]);

  return { isEarmuffed: call.earmuffed, toggleEarmuff };
};
